/**
 * What Nira holds, for the public site. coverage.json is the file backend/nira_search/scripts/ui_coverage.py writes
 * with --public (the same code the nightly release runs): rounded counts, the year-by-year strips, the court tiers, the
 * law table and the list of what is not held, with every number and date computed from the corpus and the wording that
 * says what is held and what is not curated in that script. To refresh the page, run it and replace coverage.json:
 *
 *   python scripts/ui_coverage.py --heads <state/corpus/heads-<release>.jsonl.gz> --updated-on <YYYY-MM-DD> --public --out-dir <this folder>
 *
 * The headline figures below are read from that file, so nothing here is typed by hand.
 *
 * High Court tiers (deep / good / thin) are drawn as three, two or one dot. A court drops one tier
 * when its last 12 months fall below a quarter of its 2018-24 yearly average (Kerala, today).
 *
 * Each source's density[] runs year by year from first_year: density[i] is the year first_year + i,
 * so a chart that starts at year Y uses density.slice(Y - first_year).
 */
import COVERAGE from "./coverage.json"

export { COVERAGE }

/**
 * Where a source's chart (and its "Held from" line) starts: the first year of the earliest run of 3
 * consecutive years that are each at least 10% of the source's peak year, so a few stray old orders
 * do not stretch the chart. With no such run, or fewer than 3 bars left, it is bulk_from (else the
 * first year with holdings). density[i] is the year firstYear + i.
 */
export function chartStart(
  s: { density: number[]; bulk_from?: number | null },
  firstYear: number = COVERAGE.first_year,
  lastYear: number = COVERAGE.last_year,
): number {
  const floor = Math.max(0, ...s.density) * 0.1
  if (floor > 0) {
    for (let i = 0; i + 2 < s.density.length; i++) {
      if (s.density[i] >= floor && s.density[i + 1] >= floor && s.density[i + 2] >= floor && lastYear - (firstYear + i) + 1 >= 3) return firstYear + i
    }
  }
  return fallback(s, firstYear)
}

function fallback(s: { density: number[]; bulk_from?: number | null }, firstYear: number): number {
  if (s.bulk_from) return s.bulk_from
  return firstYear + Math.max(0, s.density.findIndex((v) => v > 0))
}

const source = (key: string) => {
  const s = COVERAGE.sources.find((x) => x.key === key)
  if (!s) throw new Error(`coverage.json has no source "${key}"`)
  return s
}
/** The end of a law row's range: "2017 to Oct 2026" -> "Oct 2026". */
const heldTo = (name: string) => {
  const r = COVERAGE.law.find((l) => l.name === name)
  if (!r) throw new Error(`coverage.json has no law row "${name}"`)
  return r.range.replace(/^.* to /, "")
}
const stateActs = parseInt(COVERAGE.law.find((l) => l.name === "State GST Acts")?.range ?? "", 10)

export const COVERAGE_UPDATED = COVERAGE.updated
/** The decisions held, rounded down to a thousand ("2,98,000"). */
export const COVERAGE_TOTAL = COVERAGE.total_round

const NOTIFIED = heldTo("GST notifications")
const CIRCULATED = heldTo("CBIC circulars")

export const COVERAGE_STATS = [
  { n: source("sc").approx, l: "Supreme Court judgments" },
  { n: source("hc").approx, l: "High Court orders" },
  { n: source("cestat").approx, l: "CESTAT orders" },
  { n: source("aar").approx, l: "Advance rulings" },
  {
    n: String(stateActs),
    l: `State GST Acts, with ${NOTIFIED === CIRCULATED ? `notifications and circulars to ${NOTIFIED}` : `notifications to ${NOTIFIED} and circulars to ${CIRCULATED}`}`,
  },
]

/** What is not in yet, in Nira's own words (coverage.json "coming"). "source" is the older UIs' word for "not held". */
const STATUS_WORD: Record<string, string> = { in_progress: "In progress", next: "Next", not_held: "Not held", source: "Not held" }
export const NOT_YET = COVERAGE.coming.map((c) => ({ status: STATUS_WORD[c.status] ?? c.status, title: c.title, text: c.text }))
